document.documentElement.classList.remove('no-js');

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    if (!this.toggle || !this.panel) return;
    this.toggle.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onToggle = () => {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', String(!open));
    this.panel.hidden = open;
    document.body.classList.toggle('menu-open', !open);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
      this.toggle.click();
      this.toggle.focus();
    }
  };
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.variantData = this.querySelector('[data-product-variants]');
    this.optionGroups = [...this.querySelectorAll('[data-option-group]')];
    this.input = this.querySelector('input[name="id"]');
    this.button = this.querySelector('[data-add-button]');
    if (!this.input || !this.button) return;
    this.variants = this.variantData ? JSON.parse(this.variantData.textContent) : [];
    this.optionGroups.forEach((group) => group.addEventListener('click', this.onOptionClick));
  }

  disconnectedCallback() {
    this.optionGroups?.forEach((group) => group.removeEventListener('click', this.onOptionClick));
  }

  onOptionClick = (event) => {
    const choice = event.target.closest('[data-option-value]');
    if (!choice || choice.disabled) return;
    const group = choice.closest('[data-option-group]');
    group.querySelectorAll('[data-option-value]').forEach((button) => {
      const selected = button === choice;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    const selectedOptions = this.optionGroups.map((optionGroup) => optionGroup.querySelector('.is-selected')?.dataset.optionValue);
    const variant = this.variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));
    if (!variant) return;
    this.input.value = variant.id;
    const available = variant.available;
    this.button.disabled = !available;
    this.button.textContent = available ? this.button.dataset.availableLabel : this.button.dataset.soldOutLabel;
    if (variant.featured_media?.id) {
      document.querySelector(`[data-media-id="${variant.featured_media.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

class QuickShop extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-quick-shop-form]');
    this.variantInput = this.querySelector('[data-quick-shop-variant]');
    this.status = this.querySelector('[data-quick-shop-status]');
    this.addButton = this.querySelector('[data-quick-shop-add]');
    this.buyButton = this.querySelector('[data-quick-shop-buy]');
    this.image = this.querySelector('.quick-shop__image');
    this.optionGroups = [...this.querySelectorAll('[data-quick-shop-option]')];
    const variantData = this.querySelector('[data-quick-shop-variants]');
    if (!this.dialog || !this.form || !variantData) return;

    this.variants = JSON.parse(variantData.textContent);
    this.selected = Array(this.optionGroups.length).fill(null);
    this.openers = [...document.querySelectorAll(`[data-quick-shop-open="${this.id}"]`)];
    this.openers.forEach((button) => button.addEventListener('click', this.open));
    this.querySelector('[data-quick-shop-close]')?.addEventListener('click', this.close);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onClosed);
    this.optionGroups.forEach((group) => group.addEventListener('click', this.onOptionClick));
  }

  disconnectedCallback() {
    this.openers?.forEach((button) => button.removeEventListener('click', this.open));
    this.querySelector('[data-quick-shop-close]')?.removeEventListener('click', this.close);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    this.dialog?.removeEventListener('close', this.onClosed);
    this.optionGroups?.forEach((group) => group.removeEventListener('click', this.onOptionClick));
  }

  open = () => {
    this.dialog.showModal();
    document.body.classList.add('quick-shop-open');
  };

  close = () => {
    this.dialog.close();
  };

  onClosed = () => document.body.classList.remove('quick-shop-open');

  onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  onOptionClick = (event) => {
    const choice = event.target.closest('[data-quick-shop-value]');
    if (!choice || choice.disabled) return;
    const group = choice.closest('[data-quick-shop-option]');
    const optionIndex = Number(group.dataset.quickShopOption);
    this.selected[optionIndex] = choice.dataset.quickShopValue;

    group.querySelectorAll('[data-quick-shop-value]').forEach((button) => {
      const isSelected = button === choice;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    if (choice.dataset.quickShopImage && this.image) {
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.src = choice.dataset.quickShopImage;
      this.image.alt = choice.dataset.quickShopImageAlt || choice.dataset.quickShopValue;
    }

    this.updateAvailability();
  };

  updateAvailability() {
    this.optionGroups.forEach((group) => {
      const optionIndex = Number(group.dataset.quickShopOption);
      group.querySelectorAll('[data-quick-shop-value]').forEach((button) => {
        const candidate = [...this.selected];
        candidate[optionIndex] = button.dataset.quickShopValue;
        const possible = this.variants.some((variant) => variant.available && variant.options.every((value, index) => !candidate[index] || candidate[index] === value));
        button.disabled = !possible;
      });
    });

    const complete = this.selected.every(Boolean);
    const variant = complete
      ? this.variants.find((item) => item.options.every((value, index) => value === this.selected[index]))
      : null;
    const available = Boolean(variant?.available);

    this.variantInput.value = available ? variant.id : '';
    this.addButton.disabled = !available;
    this.buyButton.disabled = !available;
    if (!complete) {
      const missing = this.optionGroups
        .filter((group, index) => !this.selected[index])
        .map((group) => group.querySelector('legend')?.textContent.trim())
        .filter(Boolean);
      this.status.textContent = `Select ${missing.join(' and ')}.`;
    } else {
      this.status.textContent = available ? 'Your selection is ready.' : 'This combination is unavailable.';
    }
  }
}

if (!customElements.get('quick-shop')) customElements.define('quick-shop', QuickShop);

document.addEventListener('click', async (event) => {
  const buyNowButton = event.target.closest('[data-card-buy-now]');
  if (!buyNowButton || buyNowButton.disabled) return;

  const form = buyNowButton.closest('form');
  if (!form) return;

  event.preventDefault();
  buyNowButton.disabled = true;
  buyNowButton.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });

    if (!response.ok) throw new Error('Unable to add this item.');
    window.location.assign(`${window.Shopify.routes.root}checkout`);
  } catch (error) {
    buyNowButton.disabled = false;
    buyNowButton.removeAttribute('aria-busy');
    form.submit();
  }
});
